import $ from "jquery";
import * as preact from "preact";

import * as chromeExtensionInstaller from "chromeExtensionInstaller";
import * as deviceDetect from "deviceDetect";
import * as walkthrough from "walkthrough";

export const CHROME_WEBSTORE_URL =
  "https://chromewebstore.google.com/detail/readlang-web-reader/odpdkefpnfejbfnmdilmfhephfffmfoh";
export const FIREFOX_ADDON_URL =
  "https://addons.mozilla.org/firefox/addon/readlang-web-reader/";

/** Build the draggable bookmarklet href for the current host. */
export const getBookmarkletLink = () =>
  "javascript:var readlangHostname='" +
  window.location.hostname +
  "';document.getElementsByTagName('body')[0].appendChild(document.createElement('script')).setAttribute('src',window.location.protocol+'//" +
  window.location.hostname +
  "/src/translatorBookmarklet.js?bust='+new Date().getTime());";

type InstallerOptions = {
  doNotShowAgainElement?: JQuery<HTMLElement>;
  forceShowChromeExtension?: boolean;
};

type MountElement = HTMLElement | JQuery<HTMLElement>;

type InstallerProps = {
  bookmarkletLink: string;
  device: ReturnType<typeof deviceDetect.info>;
  showChrome: boolean;
};

export const showInstaller = () =>
  !walkthrough.hasAchievement("webReader") &&
  !walkthrough.hasAchievement("leavingMessage") &&
  !(
    window.chrome &&
    window.chrome.webstore &&
    $("#readlang-extension-installed").length > 0
  );

const showBookmarkletInstructions = (
  event: preact.JSX.TargetedMouseEvent<HTMLElement>
) => {
  event.stopPropagation();
  event.preventDefault();

  alert(
    "To install & use the Readlang Web Reader:\n\n" +
      "1. Drag this into your bookmarks bar.\n" +
      "2. Browse to any other webpage.\n" +
      "3. Click your Readlang Web Reader bookmark."
  );
};

const installChromeExtension = (
  event: preact.JSX.TargetedMouseEvent<HTMLAnchorElement>
) => {
  event.stopPropagation();
  event.preventDefault();
  chromeExtensionInstaller.install();
};

const WebReaderInstallerView = ({
  bookmarkletLink,
  device,
  showChrome,
}: InstallerProps) => (
  <div style={{ textAlign: "center" }}>
    {showChrome ? (
      <>
        <div class="choiceContainer">
          <a
            class="installChromeExtension"
            href={CHROME_WEBSTORE_URL}
            onClick={installChromeExtension}
          >
            <div class="choiceBox">
              <img src="/content/introStep1Crop.png" />
              <div class="overlay">
                <p style={{ marginTop: 12 }}>Chrome Extension</p>
              </div>
            </div>
          </a>
        </div>
        <div class="choiceContainer">
          <a class="installFirefoxExtension" href={FIREFOX_ADDON_URL}>
            <div class="choiceBox">
              <img src="/content/introStep1Crop.png" />
              <div class="overlay">
                <p style={{ marginTop: 12 }}>Firefox Extension</p>
              </div>
            </div>
          </a>
        </div>
      </>
    ) : null}
    <div class="choiceContainer">
      {device.iOS && !device.chrome ? (
        <a href="https://forum.readlang.com/t/install-the-readlang-web-reader-on-ios-safari/48">
          <BookmarkletChoiceBox device={device} />
        </a>
      ) : null}
      {device.iOS && device.chrome ? (
        <a href="https://forum.readlang.com/t/install-readlang-web-reader-on-chrome-for-ios/49">
          <BookmarkletChoiceBox device={device} />
        </a>
      ) : null}
      {device.android ? (
        <a href="https://forum.readlang.com/t/install-readlang-web-reader-on-chrome-for-android/50">
          <BookmarkletChoiceBox device={device} />
        </a>
      ) : null}
      {!device.iOS && !device.android ? (
        <a data-bypass="true" href={bookmarkletLink}>
          <BookmarkletChoiceBox
            device={device}
            onClick={showBookmarkletInstructions}
            showChrome={showChrome}
          />
        </a>
      ) : null}
    </div>
  </div>
);

const BookmarkletChoiceBox = ({
  device,
  onClick,
  showChrome,
}: {
  device: ReturnType<typeof deviceDetect.info>;
  onClick?: (event: preact.JSX.TargetedMouseEvent<HTMLElement>) => void;
  showChrome?: boolean;
}) => (
  <div
    class={["choiceBox", !device.iOS && !device.android ? "clickable" : ""]
      .filter(Boolean)
      .join(" ")}
    onClick={onClick}
  >
    <img src="/content/installBookmarklet.png" />
    <p>Install Bookmarklet</p>
    {!device.iOS && !device.android ? (
      <div class="overlay">
        {showChrome ? <small>or</small> : null} Drag this bookmarklet into your
        bookmarks bar
      </div>
    ) : null}
    {device.iOS ? (
      <div class="overlay oneLine">
        {device.chrome ? "Install on iOS Chrome" : "Install on iOS Safari"}
      </div>
    ) : null}
    {device.android ? (
      <div class="overlay oneLine">
        Install on Android{device.chrome ? " Chrome" : ""}
      </div>
    ) : null}
  </div>
);

const DoNotShowAgainView = () => {
  const toggle = (event: preact.JSX.TargetedEvent<HTMLInputElement>) => {
    const checked = event.currentTarget.checked;
    const hasAchievement = walkthrough.hasAchievement("leavingMessage");

    if (!hasAchievement && checked) {
      walkthrough.setAchievement("leavingMessage");
    }
    if (hasAchievement && !checked) {
      walkthrough.setAchievement("leavingMessage", false);
    }
  };

  return (
    <label>
      <input class="doNotShowAgain" onClick={toggle} type="checkbox" />
      Don't show this message again, I already have the Web Reader.
    </label>
  );
};

const getMountElement = (element: MountElement): HTMLElement => {
  if (element instanceof HTMLElement) {
    return element;
  }

  return element[0];
};

export const render = (
  element: MountElement,
  options: InstallerOptions = {}
) => {
  const device = deviceDetect.info();
  const bookmarkletLink = getBookmarkletLink();

  preact.render(
    <WebReaderInstallerView
      bookmarkletLink={bookmarkletLink}
      device={device}
      showChrome={
        options.forceShowChromeExtension ||
        (!deviceDetect.is("iOS") && !deviceDetect.is("android"))
      }
    />,
    getMountElement(element)
  );

  if (options.doNotShowAgainElement) {
    preact.render(<DoNotShowAgainView />, options.doNotShowAgainElement[0]);
  }
};
